import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../../api/axios';
import { formatDate } from '../../utils/dateUtils';
import { FiUsers, FiFileText, FiDollarSign, FiClock, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await API.get('/admin/dashboard');
        setData(res.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const getStatusBadge = (status) => {
    const map = {
      pending: 'En attente', partial: 'Partiel', paid: 'Payée',
      overdue: 'En retard', cancelled: 'Annulée',
    };
    return <span className={`badge badge-${status}`}>{map[status] || status}</span>;
  };

  const providerNames = {
    mpesa: 'M-Pesa', orange_money: 'Orange Money',
    airtel_money: 'Airtel Money', afrimoney: 'Afrimoney',
  };

  if (loading) {
    return <div className="loading-page"><div className="spinner"></div><p>Chargement du tableau de bord...</p></div>;
  }

  if (!data) return null;

  const { stats, recentPayments, recentInvoices } = data;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 600 }}>Tableau de bord</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Vue d'ensemble de l'activité NomargueriPay</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><FiUsers /></div>
          <div className="stat-info">
            <h3>{stats.totalPatients}</h3>
            <p>Patients inscrits</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><FiFileText /></div>
          <div className="stat-info">
            <h3>{stats.totalInvoices}</h3>
            <p>Total factures</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><FiCheckCircle /></div>
          <div className="stat-info">
            <h3>{stats.paidInvoices}</h3>
            <p>Factures payées</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red"><FiAlertTriangle /></div>
          <div className="stat-info">
            <h3>{stats.pendingInvoices}</h3>
            <p>En attente</p>
          </div>
        </div>
      </div>

      {/* Revenus */}
      <div className="stats-grid stats-grid-2">
        <div className="stat-card">
          <div className="stat-icon green"><FiDollarSign /></div>
          <div className="stat-info">
            <h3>{parseFloat(stats.totalRevenueCDF).toLocaleString()} FC</h3>
            <p>Revenus (CDF)</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><FiDollarSign /></div>
          <div className="stat-info">
            <h3>{parseFloat(stats.totalRevenueUSD).toLocaleString()} $</h3>
            <p>Revenus (USD)</p>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Dernières factures */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Dernières factures</span>
            <Link to="/admin/invoices" className="btn btn-outline btn-sm">Voir tout</Link>
          </div>
          {recentInvoices.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Aucune facture</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Patient</th>
                    <th>Montant</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{inv.invoice_number}</td>
                      <td>{inv.patient?.first_name} {inv.patient?.last_name}</td>
                      <td style={{ fontWeight: 600 }}>{parseFloat(inv.amount).toLocaleString()} {inv.currency}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Derniers paiements */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Derniers paiements</span>
            <Link to="/admin/payments" className="btn btn-outline btn-sm">Voir tout</Link>
          </div>
          {recentPayments.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Aucun paiement</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Montant</th>
                    <th>Opérateur</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPayments.map((p) => (
                    <tr key={p.id}>
                      <td>{p.patient?.first_name} {p.patient?.last_name}</td>
                      <td style={{ fontWeight: 600 }}>{parseFloat(p.amount).toLocaleString()} {p.currency}</td>
                      <td>{providerNames[p.provider] || p.provider}</td>
                      <td>{formatDate(p.paid_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
